import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import {
  applyModeLabel as formatApplyMode,
  portalLabel,
  resolveSupportTierKey,
  supportTierCompact,
  certifiedCoverage,
} from '../utils/applyDisclosure';

// ── Label humanizer — converts raw snake_case keys to readable words ──────────
function humanizeLabel(label) {
  if (!label || label.includes(' ')) return label;
  return label
    .replace(/^(taleo|workday|greenhouse|custom)_/i, '')
    .replace(/_+/g, ' ')
    .trim();
}

// ── Essay question detection ──────────────────────────────────────────────────
const ESSAY_BLOCK = [
  /\b(start\s*date|earliest\s*date|graduation\s*date|expected\s*date|date\s*of\s*birth)\b/i,
  /\bwhen\s+(will|can|do|are|were|did|would|could|might)\s+you\b/i,
  /\b(finish\s*(?:your\s*)?studi|notice\s*period|how\s+long|duration|number\s+of\s+(?:months|years|weeks))\b/i,
  /\bprefer\s+to\s+start\b/i,
  /\b(gpa|grade\s*point|cumulative\s*gr?ade|degree\s*class|sat\s*score|act\s*score)\b/i,
  /\b(hourly\s*rate|per\s*hour|salary|compensation|expected\s*pay|wage|stipend)\b/i,
  /\b(phone\s*number|mobile\s*number|email\s*address|address\s*line|zip\s*code|postal\s*code|nationality|gender)\b/i,
  /\b(linkedin\s*(profile|url)?|github\s*(profile|url)?|portfolio\s*(url|link)?|personal\s*(website|site|url))\b/i,
  /\b(family\s*member|currently\s*employed\s*by|personal\s*connection)\b/i,
  /\b(language\s*(level|skill|proficiency)|fluent\s*in|\bA[12]\b|\bB[12]\b|\bC[12]\b)\b/i,
  /\bare\s+you\s+(currently|able|available|enrolled|authorized|eligible)\b/i,
  /\bhave\s+you\s+(ever|previously|completed\s+any|worked\s+at|applied)\b/i,
  /\bdo\s+you\s+(speak|hold|possess|require\s+(?:a\s+)?(?:visa|sponsorship))\b/i,
  /\bwhat\s+is\s+your\s+(name|email|phone|grade|gpa|score|number)\b/i,
];

const ESSAY_ALLOW = [
  /\bwhy\b.{0,60}(company|role|position|firm|team|us\b|this\b|join|apply|want|choose)/i,
  /\bwhy\b.{1,40}\?/i,
  /\b(motivation|most\s+import|what\s+excit|interest(?:ed)?\s+in|appeal|attracts?\s+you|drawn\s+to)\b/i,
  /\b(describe|tell\s+us|please\s+(?:give|provide|share|describe|write|explain|outline)|elaborate|discuss)\b/i,
  /\bhow\s+(?:have|has|would|did|do)\s+you\b/i,
  /\b(?:financial\s*market|news\s*item|market[s]?\s*driven|recently\s*driven)\b/i,
  /\b(challenge|achievement|accomplishment|impact|strength|weakness)\b/i,
  /\b(career\s*(?:goal|aspiration|plan)|most\s+important|expecting\s+from)\b/i,
];

function isEssayQuestion(label) {
  if (!label) return false;
  const t = label.trim();
  if (t.length < 8 || t.length > 500) return false;
  if (ESSAY_BLOCK.some((re) => re.test(t))) return false;
  if (t.length > 50) return true;
  return ESSAY_ALLOW.some((re) => re.test(t));
}

function hasMeaningfulValue(v) {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

const FIELD_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone: 'Phone number',
  university: 'University',
  degree: 'Degree',
  graduation_year: 'Graduation year',
  linkedin_url: 'LinkedIn URL',
  current_location: 'Current location',
  country: 'Country',
  work_authorization: 'Work authorization',
  requires_sponsorship: 'Requires sponsorship',
  portfolio_url: 'Portfolio URL',
  website_url: 'Website URL',
  gender: 'Gender identity',
};

function FieldInput({ name, value, onChange, type = 'text', placeholder }) {
  if (name === 'requires_sponsorship') {
    return (
      <select
        value={String(Boolean(value))}
        onChange={(e) => onChange(name, e.target.value === 'true')}
        style={inputStyle}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    );
  }

  if (name === 'work_authorization') {
    return (
      <select value={value || ''} onChange={(e) => onChange(name, e.target.value)} style={inputStyle}>
        <option value="">Select work authorization</option>
        <option value="Authorized to work without sponsorship">Authorized to work without sponsorship</option>
        <option value="Requires visa sponsorship">Requires visa sponsorship</option>
        <option value="Need employer clarification">Need employer clarification</option>
      </select>
    );
  }

  return (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(name, e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

function SingleFieldInput({ field, value, onChange, allAnswers = {}, onExtraChange }) {
  if (!field) return null;

  if (field.type === 'textarea') {
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        rows={4}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }}
      />
    );
  }

  if (field.type === 'date') {
    const shouldUseTodayMin = field.min === 'today' || field.name === 'taleo_start_date';
    return (
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        style={inputStyle}
        min={shouldUseTodayMin ? new Date().toISOString().split('T')[0] : undefined}
      />
    );
  }

  if (field.type === 'multi_value_single_select') {
    return (
      <select value={value || ''} onChange={(e) => onChange(field.name, e.target.value)} style={inputStyle}>
        <option value="">Select an answer</option>
        {(field.values || []).map((option) => (
          <option key={`${field.name}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'multi_value_multi_select') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(field.values || []).map((option) => (
          <label key={`${field.name}-${option.value}`} style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={(e) => {
                if (e.target.checked) onChange(field.name, [...selected, option.value]);
                else onChange(field.name, selected.filter((entry) => entry !== option.value));
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (field.type === 'experience_multi_select') {
    const selected = Array.isArray(value) ? value : [];
    const currentOverrides = Array.isArray(allAnswers?.greenhouse_current_overrides)
      ? allAnswers.greenhouse_current_overrides
      : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(field.values || []).map((option) => {
          const isSelected = selected.includes(option.value);
          const isCurrentOverride = currentOverrides.map(String).includes(String(option.value));
          return (
            <div key={`${field.name}-${option.value}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ ...checkboxRowStyle, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    if (e.target.checked) onChange(field.name, [...selected, option.value]);
                    else {
                      onChange(field.name, selected.filter((entry) => entry !== option.value));
                      onExtraChange?.('greenhouse_current_overrides',
                        currentOverrides.filter((v) => String(v) !== String(option.value)));
                    }
                  }}
                />
                <span>
                  <strong style={{ display: 'block', color: '#0B1220' }}>{option.label}</strong>
                  {option.meta?.date_label && (
                    <small style={{ display: 'block', marginTop: 3, color: '#64748B', fontSize: 12 }}>
                      {option.meta.date_label}
                    </small>
                  )}
                </span>
              </label>
              {isSelected && (
                <label style={{ ...checkboxRowStyle, marginLeft: 26, fontSize: 12, color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={isCurrentOverride || Boolean(option.meta?.current)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onExtraChange?.('greenhouse_current_overrides', [...currentOverrides, option.value]);
                      } else {
                        onExtraChange?.('greenhouse_current_overrides',
                          currentOverrides.filter((v) => String(v) !== String(option.value)));
                      }
                    }}
                  />
                  <span>Current role</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (field.type === 'boolean_checkbox') {
    return (
      <label style={checkboxRowStyle}>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(field.name, e.target.checked)}
        />
        <span>Yes, I confirm</span>
      </label>
    );
  }

  return (
    <input
      value={value || ''}
      onChange={(e) => onChange(field.name, e.target.value)}
      style={inputStyle}
    />
  );
}

function EssayGenerateButton({ questionLabel, internshipId, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setDone(false);
    try {
      const res = await api.post('/ai/generate-modal-essay', {
        internship_id: internshipId,
        question_text: questionLabel,
      });
      onGenerated(res.data.answer);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={handleGenerate}
        disabled={loading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8, border: 'none',
          cursor: loading ? 'default' : 'pointer',
          background: done ? '#0B1220' : 'var(--tandm-gradient-cta)',
          color: '#fff', fontSize: 13, fontWeight: 600,
          fontFamily: 'var(--tandm-font-sans)',
          boxShadow: '0 2px 8px rgba(196,98,45,0.22)',
          opacity: loading ? 0.75 : 1,
          transition: 'opacity 0.15s, background 0.2s',
        }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
        {loading ? 'Generating…' : done ? '✓ Generated — review and edit above' : 'Generate answer with AI'}
      </button>
      {error && (
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#c0392b' }}>{error}</p>
      )}
    </div>
  );
}

function QuestionInput({ question, allAnswers = {}, onChange }) {
  const fields = question.fields || [];
  if (fields.length === 0) return null;

  if (fields.length === 1) {
    return (
      <SingleFieldInput
        field={fields[0]}
        value={allAnswers[fields[0].name]}
        onChange={onChange}
        allAnswers={allAnswers}
        onExtraChange={onChange}
      />
    );
  }

  // Multi-field question (e.g. education month + year) — render inline pair
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {fields.map((field) => (
        <div key={field.name} style={{ flex: 1 }}>
          <SingleFieldInput
            field={field}
            value={allAnswers[field.name]}
            onChange={onChange}
            allAnswers={allAnswers}
            onExtraChange={onChange}
          />
        </div>
      ))}
    </div>
  );
}

function applicationRecordedCopy(application) {
  const submission = application?.submission_status;
  if (application?.status === 'submitted' || submission === 'confirmed_auto_submit') {
    return 'Tandm recorded this as submitted to the employer.';
  }
  if (submission === 'submitted_unconfirmed') {
    return 'Tandm clicked Submit on Workday. Confirm on the employer page if you still see a review screen.';
  }
  if (application?.status === 'handoff_started') {
    return 'Your packet is ready. Finish submit on the employer portal, then mark it submitted here if needed.';
  }
  return 'Your application state has been updated.';
}

export default function ApplyFlowModal({ open, internship, onClose, onApplicationUpdated }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('readiness');
  const [error, setError] = useState('');
  const [packet, setPacket] = useState(null);
  const [application, setApplication] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [profileFields, setProfileFields] = useState({});
  const [answers, setAnswers] = useState({});
  const [coverLetter, setCoverLetter] = useState('');
  const [coverLetterLoading, setCoverLetterLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [handoffState, setHandoffState] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [showResolvedQuestions, setShowResolvedQuestions] = useState(false);
  const [wdVerifyLoading, setWdVerifyLoading] = useState(false);
  const [wdAccountStatus, setWdAccountStatus] = useState(null);
  const [wdSignupEvidence, setWdSignupEvidence] = useState(null);

  useEffect(() => {
    if (!open || !internship?.id) return;

    let active = true;
    setLoading(true);
    setError('');
    setStep('readiness');
    setCoverLetter('');
    setConsent(false);
    setShowPassword(false);
    setShowResolvedQuestions(false);
    setWdSignupEvidence(null);

    api.post('/apply/prepare', { internship_id: internship.id })
      .then((res) => {
        if (!active) return;
        const nextPacket = res.data.packet;
        setPacket(nextPacket);
        setApplication(res.data.application);
        setMissingFields(res.data.missing_fields || []);
        setProfileFields(nextPacket?.user_profile || {});
        setAnswers(nextPacket?.suggested_answers || {});
        setWdAccountStatus(nextPacket?.workday_account?.status || null);
        setWdSignupEvidence(nextPacket?.workday_account?.signup_evidence || null);
      })
      .catch((err) => {
        if (!active) return;
        if (err.response?.status === 401) {
          onClose();
          navigate('/login');
          return;
        }
        setError(err.response?.data?.error || 'Could not prepare this application.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, internship?.id, navigate, onClose]);

  // The extension submits the account in the employer tab. Refresh when the
  // student returns so this modal reflects what actually happened on Workday.
  useEffect(() => {
    if (!open || !internship?.id || wdAccountStatus !== 'pending_verification') return undefined;

    const refreshWorkdayAccount = async () => {
      try {
        const res = await api.get(`/apply/workday/account/${internship.id}`);
        setWdAccountStatus(res.data?.status || null);
        setWdSignupEvidence(res.data?.signup_evidence || null);
      } catch {
        // Keep the last known state; prepare/apply errors are surfaced elsewhere.
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshWorkdayAccount();
    };

    window.addEventListener('focus', refreshWorkdayAccount);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshWorkdayAccount);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [open, internship?.id, wdAccountStatus]);

  const applyModeLabel = useMemo(() => {
    if (!packet) return '';
    if (packet.apply_mode === 'one_click') return 'One-click';
    if (packet.apply_mode === 'fast_apply') return 'Fast apply';
    if (packet.apply_mode === 'manual') return 'Manual';
    return formatApplyMode(packet.apply_mode);
  }, [packet]);

  const unresolvedRequired = useMemo(() => {
    if (!packet?.questions) return [];
    return packet.questions.filter((q) =>
      q.required &&
      q.fields.some((f) => !hasMeaningfulValue(answers[f.name]))
    );
  }, [packet, answers]);

  const needsAttentionQuestions = useMemo(() => {
    if (!packet?.questions) return [];
    return packet.questions.filter((q) => !q.resolved || q.low_confidence);
  }, [packet]);

  const resolvedQuestions = useMemo(() => {
    if (!packet?.questions) return [];
    return packet.questions.filter((q) => q.resolved && !q.low_confidence);
  }, [packet]);

  const updateProfileField = (name, value) => setProfileFields((prev) => ({ ...prev, [name]: value }));
  const updateAnswer = (name, value) => setAnswers((prev) => ({ ...prev, [name]: value }));

  // Auto-save answers as student fills them (1.5s debounce)
  useEffect(() => {
    if (!internship?.id || !packet) return;
    const hasAnswers = Object.keys(answers).length > 0;
    if (!hasAnswers) return;

    setAutoSaveStatus('Saving...');
    const timer = setTimeout(() => {
      api.post('/apply/save-draft', { internship_id: internship.id, answers })
        .then(() => {
          setAutoSaveStatus('Saved ✓');
          setTimeout(() => setAutoSaveStatus(''), 2500);
        })
        .catch(() => setAutoSaveStatus(''));
    }, 1500);

    return () => clearTimeout(timer);
  }, [answers, internship?.id, packet]);

  const generateCoverLetter = async () => {
    setCoverLetterLoading(true);
    setError('');
    try {
      const res = await api.post('/ai/generate-cover-letter', {
        internship_id: internship.id,
        questions: (packet?.questions || []).map((question) => question.label),
      });
      setCoverLetter(res.data.content || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not generate a cover letter right now.');
    } finally {
      setCoverLetterLoading(false);
    }
  };

  const proceedToReview = () => {
    setError('');
    setStep('review');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setStep('submitting');

    try {
      if (packet.apply_mode === 'one_click') {
        const res = await api.post('/apply/submit', {
          internship_id: internship.id,
          confirmed_fields: profileFields,
          answers,
          cover_letter: coverLetter,
          consent,
        });
        setApplication(res.data.application);
        onApplicationUpdated?.(res.data.application);
        setStep('success');
      } else {
        const res = await api.post('/apply/handoff', {
          internship_id: internship.id,
          confirmed_fields: profileFields,
          answers,
          cover_letter: coverLetter,
          consent,
        });
        setApplication(res.data.application);
        setHandoffState(res.data);
        onApplicationUpdated?.(res.data.application);
        setStep('handoff');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong while applying.');
      setStep('review');
    } finally {
      setLoading(false);
    }
  };

  const confirmManualCompletion = async (completed) => {
    if (!application?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/apply/complete-manual', {
        application_id: application.id,
        completed,
      });
      setApplication(res.data.application);
      onApplicationUpdated?.(res.data.application);
      setStep(completed ? 'success' : 'handoff');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update your application status.');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (loading) return;
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={overlayStyle}
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            style={panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
              <div>
                <p style={eyebrowStyle}>{applyModeLabel}</p>
                <h2 style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 800, color: '#0B1220', letterSpacing: '-0.04em' }}>
                  Apply to {internship?.company_name}
                </h2>
                <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
                  {packet?.apply_reason || internship?.apply_reason || 'Tandm will prepare your application packet and guide the next step.'}
                </p>
              </div>
              <button onClick={close} style={closeButtonStyle}>Close</button>
            </div>

            {error && (
              <div style={errorBoxStyle}>{error}</div>
            )}

            {loading && !packet ? (
              <div style={centerStateStyle}>Preparing your application packet...</div>
            ) : null}

            {!loading && packet && step === 'readiness' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={infoCardStyle}>
                  <p style={sectionTitleStyle}>Readiness check</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {missingFields.length === 0 ? (
                      <div style={successPillStyle}>Your profile is ready for this application.</div>
                    ) : (
                      missingFields.map((field) => (
                        <div key={field} style={warningRowStyle}>{field}</div>
                      ))
                    )}
                  </div>
                </div>

                <div style={infoCardStyle}>
                  <p style={sectionTitleStyle}>What Tandm has prepared</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                      ['Resume on file', packet.user_profile.cv_url ? 'Ready' : 'Missing'],
                      ['Profile snapshot', packet.user_profile.first_name ? 'Ready' : 'Needs review'],
                      [
                        'Question memory',
                        packet.question_summary?.saved_answers_count
                          ? `${packet.question_summary.saved_answers_count} answers remembered`
                          : 'No saved answers yet'
                      ],
                      [
                        'Questions to review',
                        packet.question_summary?.unresolved_questions_count
                          ? `${packet.question_summary.unresolved_questions_count} still needed`
                          : 'No new questions'
                      ],
                      ['Cover letter', packet.cover_letter_required ? 'Recommended for this role' : 'Optional'],
                      ['Employer portal', portalLabel(packet.portal_type) || 'Employer portal'],
                      [
                        'Support tier',
                        (() => {
                          const key = resolveSupportTierKey(packet);
                          const compact = supportTierCompact(key);
                          const coverage = certifiedCoverage(packet);
                          const base = packet.apply_support_label
                            || (compact ? compact : (key ? String(key).replace(/_/g, ' ') : 'Guided'));
                          return coverage != null ? `${base} · ${coverage}% coverage` : base;
                        })(),
                      ],
                      [
                        'Filled vs needs review',
                        `${packet.question_summary?.saved_answers_count || 0} ready · ${packet.question_summary?.unresolved_questions_count || 0} to review`,
                      ],
                    ].map(([label, value]) => (
                      <div key={label} style={miniCardStyle}>
                        <span style={{ display: 'block', fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>{label}</span>
                        <strong style={{ color: '#0B1220', fontSize: 13 }}>{value}</strong>
                      </div>
                    ))}
                  </div>
                  {packet.greenhouse_risk_flags?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                      {packet.greenhouse_risk_flags.slice(0, 3).map((flag) => (
                        <div key={flag.code || flag.message} style={warningRowStyle}>
                          {flag.message || flag.code}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'var(--tandm-accent-subtle)',
                  border: '1px solid var(--tandm-line)',
                  fontSize: 13,
                  color: 'var(--tandm-accent-deep)',
                  lineHeight: 1.55,
                }}>
                  <strong style={{ color: 'var(--tandm-ink)' }}>You submit.</strong>
                  {' '}Tandm fills the form in your browser — review everything before you send it.
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={proceedToReview} style={primaryButtonStyle}>Review application</button>
                </div>
              </div>
            )}

            {packet && step === 'review' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                {(packet.greenhouse_support_tier || packet.greenhouse_ready_status) && (() => {
                  const key = packet.greenhouse_support_tier || packet.greenhouse_certification?.support_tier || packet.greenhouse_ready_status;
                  const tier = READINESS_TIER_CONFIG[key] || READINESS_TIER_CONFIG.blocked;
                  return (
                    <div style={{ padding: '11px 14px', borderRadius: 14, border: `1px solid ${tier.border}`, background: tier.bg, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: tier.color }}>{packet.greenhouse_certification?.label || tier.label}</span>
                        {packet.greenhouse_coverage_score != null && (
                          <span style={{ fontSize: 12, color: tier.color, opacity: 0.8 }}>{packet.greenhouse_coverage_score}% field coverage</span>
                        )}
                      </div>
                      {packet.greenhouse_certification?.requires_student_review && (
                        <span style={{ fontSize: 12, lineHeight: 1.5, color: tier.color, opacity: 0.85 }}>
                          {packet.greenhouse_certification?.warnings?.[0]?.message || 'Please review the highlighted answers before opening the employer portal.'}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {packet.portal_type === 'workday' && packet.apply_support_tier && (() => {
                  const key = packet.apply_support_tier;
                  const tier = READINESS_TIER_CONFIG[key] || READINESS_TIER_CONFIG.guided_handoff;
                  const label = packet.apply_support_label || tier.label;
                  const reason = packet.apply_certification?.support_reason || packet.apply_reason || null;
                  return (
                    <div style={{ padding: '11px 14px', borderRadius: 14, border: `1px solid ${tier.border}`, background: tier.bg, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: tier.color }}>{label}</span>
                      {reason && (
                        <span style={{ fontSize: 12, lineHeight: 1.5, color: tier.color, opacity: 0.85 }}>{reason}</span>
                      )}
                    </div>
                  );
                })()}

                {/* Credentials exist locally before Workday receives the signup. */}
                {packet.portal_type === 'workday' && wdAccountStatus === 'pending_verification' && !wdSignupEvidence && packet.workday_account?.email && (
                  <div style={{ padding: '14px 16px', borderRadius: 14, border: '1px solid #BFDBFE', background: '#EFF6FF', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1E3A8A' }}>Workday account setup is ready</span>
                    <span style={{ fontSize: 13, color: '#1E40AF', lineHeight: 1.6 }}>
                      Continue with fast apply and open the employer portal. The Tandm extension will create the Workday account for <strong>{packet.workday_account.email}</strong>. Workday will send the verification email after it accepts that signup.
                    </span>
                  </div>
                )}

                {/* Ask for verification only after the extension confirms submission. */}
                {packet.portal_type === 'workday' && wdAccountStatus === 'pending_verification' && wdSignupEvidence && packet.workday_account?.email && (
                  <div style={{ padding: '14px 16px', borderRadius: 14, border: '1px solid #FDE68A', background: '#FFFBEB', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#92400E' }}>Verify your Workday account first</span>
                    <span style={{ fontSize: 13, color: '#78350F', lineHeight: 1.6 }}>
                      Workday accepted the account signup for <strong>{packet.workday_account.email}</strong>. Check your inbox and click the verification link, then come back and click below.
                    </span>
                    <button
                      disabled={wdVerifyLoading}
                      onClick={async () => {
                        setWdVerifyLoading(true);
                        try {
                          const res = await api.post(`/apply/workday/account/${internship.id}/verify`);
                          if (res.data?.ok) {
                            setWdAccountStatus('active');
                          } else if (res.data?.error === 'signup_not_submitted') {
                            alert(res.data.message || 'Click Apply on the job first so Tandm can register your Workday account, then verify the email.');
                          }
                        } catch {
                          alert('Could not confirm verification — please try again.');
                        } finally {
                          setWdVerifyLoading(false);
                        }
                      }}
                      style={{ alignSelf: 'flex-start', padding: '8px 16px', background: '#92400E', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: wdVerifyLoading ? 'wait' : 'pointer' }}
                    >
                      {wdVerifyLoading ? 'Confirming…' : "I've verified my email"}
                    </button>
                  </div>
                )}

                {needsAttentionQuestions.length > 0 && (
                  <div style={infoCardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <p style={{ ...sectionTitleStyle, margin: 0 }}>Questions needing your input</p>
                      {autoSaveStatus && (
                        <span style={{ fontSize: 11, color: autoSaveStatus.includes('✓') ? '#3B7A57' : '#64748B', fontWeight: 600 }}>
                          {autoSaveStatus}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>
                      Answer these questions — Tandm will prefill them on the employer portal. Your answers are auto-saved as you type.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {needsAttentionQuestions.map((question) => {
                        if (question.conditions) {
                          const { field, operator, value } = question.conditions;
                          const currentAnswer = String(answers[field] ?? '');
                          const conditionMet = operator === 'eq'
                            ? currentAnswer === String(value)
                            : operator === 'neq'
                              ? currentAnswer !== String(value)
                              : Boolean(currentAnswer);
                          if (!conditionMet) return null;
                        }
                        return (
                          <div key={question.fields[0]?.name || question.label}>
                            <label style={labelStyle}>
                              {humanizeLabel(question.label)}
                              {question.required ? ' *' : ''}
                              {question.low_confidence && (
                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '1px 5px', verticalAlign: 'middle' }}>Review</span>
                              )}
                            </label>
                            {(question.hint || question.description) && (
                              <p style={{ margin: '2px 0 6px', fontSize: 12, color: '#9A8070', lineHeight: 1.5 }}>
                                {question.hint || question.description}
                              </p>
                            )}
                            <QuestionInput
                              question={question}
                              allAnswers={answers}
                              onChange={updateAnswer}
                            />
                            {question.fields.some((f) => f.type === 'textarea') &&
                              isEssayQuestion(question.label) && (
                              <EssayGenerateButton
                                questionLabel={question.label}
                                internshipId={internship.id}
                                onGenerated={(answer) => {
                                  const taField = question.fields.find((f) => f.type === 'textarea');
                                  if (taField) updateAnswer(taField.name, answer);
                                }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {needsAttentionQuestions.length === 0 && resolvedQuestions.length === 0 && (
                  <div style={infoCardStyle}>
                    <p style={sectionTitleStyle}>No new questions</p>
                    <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
                      Tandm already knows the reusable answers for this role, so after your final review it can move straight into the employer handoff.
                    </p>
                  </div>
                )}

                {resolvedQuestions.length > 0 && (
                  <div style={infoCardStyle}>
                    <button
                      onClick={() => setShowResolvedQuestions((v) => !v)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--tandm-font-sans)' }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#0B1220' }}>
                        Pre-filled answers ({resolvedQuestions.length})
                      </span>
                      <span style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>
                        {showResolvedQuestions ? 'Hide' : 'Review'}
                      </span>
                    </button>
                    {!showResolvedQuestions && (
                      <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>
                        Tandm has already resolved {resolvedQuestions.length} {resolvedQuestions.length === 1 ? 'question' : 'questions'} from your profile and answer memory.
                      </p>
                    )}
                    {showResolvedQuestions && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 14 }}>
                        {resolvedQuestions.map((question) => {
                          if (question.conditions) {
                            const { field, operator, value } = question.conditions;
                            const currentAnswer = String(answers[field] ?? '');
                            const conditionMet = operator === 'eq'
                              ? currentAnswer === String(value)
                              : operator === 'neq'
                                ? currentAnswer !== String(value)
                                : Boolean(currentAnswer);
                            if (!conditionMet) return null;
                          }
                          return (
                            <div key={question.fields[0]?.name || question.label} style={{ opacity: 0.85 }}>
                              <label style={labelStyle}>
                                {humanizeLabel(question.label)}
                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#065F46', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 6, padding: '1px 5px', verticalAlign: 'middle' }}>Pre-filled</span>
                              </label>
                              {(question.hint || question.description) && (
                                <p style={{ margin: '2px 0 6px', fontSize: 12, color: '#9A8070', lineHeight: 1.5 }}>
                                  {question.hint || question.description}
                                </p>
                              )}
                              <QuestionInput
                                question={question}
                                allAnswers={answers}
                                onChange={updateAnswer}
                              />
                              {question.fields.some((f) => f.type === 'textarea') &&
                                isEssayQuestion(question.label) && (
                                <EssayGenerateButton
                                  questionLabel={question.label}
                                  internshipId={internship.id}
                                  onGenerated={(answer) => {
                                    const taField = question.fields.find((f) => f.type === 'textarea');
                                    if (taField) updateAnswer(taField.name, answer);
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {packet.portal_type === 'taleo' && user?.portal_account_password && (
                  <div style={infoCardStyle}>
                    <p style={sectionTitleStyle}>Employer portal password</p>
                    <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>
                      This portal requires an account. Tandm will register you automatically using the password below — save it for future logins to this employer portal.
                      {' '}If a robot check appears on the employer site, click it — Tandm will continue once it is done.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <code style={{
                        flex: 1, padding: '10px 14px', background: '#EEF2F4', borderRadius: 8,
                        fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#162033',
                        letterSpacing: '0.05em',
                      }}>
                        {showPassword ? user.portal_account_password : '••••••••••••••••'}
                      </code>
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        style={{ background: 'none', border: '1px solid #D9E2E6', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: '#64748B', fontSize: 12, fontWeight: 700, fontFamily: 'var(--tandm-font-sans)' }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(user.portal_account_password)}
                        style={{ background: '#4E6C7E', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'var(--tandm-font-sans)' }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                <div style={infoCardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
                    <div>
                      <p style={sectionTitleStyle}>Cover letter</p>
                      <p style={{ margin: 0, fontSize: 13, color: '#64748B' }}>
                        {packet.cover_letter_required ? 'This role likely expects a cover letter.' : 'Optional, but often worth including.'}
                      </p>
                    </div>
                    <button onClick={generateCoverLetter} disabled={coverLetterLoading} style={secondaryButtonStyle}>
                      {coverLetterLoading ? 'Generating...' : (coverLetter ? 'Regenerate' : 'Generate')}
                    </button>
                  </div>
                  <textarea
                    value={coverLetter}
                    onChange={(e) => setCoverLetter(e.target.value)}
                    rows={10}
                    placeholder="Generate or paste the cover letter you want Tandm to use."
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 220 }}
                  />
                </div>

                <label style={checkboxRowStyle}>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span>I confirm Tandm can use this information to submit or prepare this application on my behalf.</span>
                </label>

                {unresolvedRequired.length > 0 && (
                  <div style={warningRowStyle}>
                    {unresolvedRequired.length === 1
                      ? '1 required question above still needs an answer before you can apply.'
                      : `${unresolvedRequired.length} required questions above still need answers before you can apply.`}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <button onClick={() => setStep('readiness')} style={ghostButtonStyle}>Back</button>
                  <button
                    onClick={handleSubmit}
                    disabled={!consent || loading || unresolvedRequired.length > 0}
                    style={primaryButtonStyle}
                  >
                    {packet.apply_mode === 'one_click' ? 'Submit with Tandm' : 'Start fast apply'}
                  </button>
                </div>
              </div>
            )}

            {step === 'submitting' && (
              <div style={centerStateStyle}>
                {packet?.apply_mode === 'one_click' ? 'Submitting to the employer portal...' : 'Preparing your fast-apply handoff...'}
              </div>
            )}

            {step === 'handoff' && handoffState && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={infoCardStyle}>
                  <p style={sectionTitleStyle}>Fast apply is ready</p>
                  <p style={{ margin: '0 0 12px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
                    {handoffState.autofill_supported
                      ? 'Tandm has prepared an autofill packet for this portal. Open the employer page in Chrome with the Tandm extension installed, let it fill the form, then you submit.'
                      : 'Tandm has prepared your profile, answers, and cover letter. Open the employer portal, finish submit yourself, then come back here to confirm.'}
                  </p>
                  {handoffState.autofill_supported && (
                    <div style={{ ...miniCardStyle, marginBottom: 12 }}>
                      <span style={{ display: 'block', fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>Autofill support</span>
                      <strong style={{ color: '#0B1220', fontSize: 13 }}>
                        Tandm browser autofill is available for supported portals through the extension in this repo.
                      </strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {handoffState.checklist?.map((item) => (
                      <div key={item} style={warningRowStyle}>{item}</div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <a
                    href={handoffState.handoff_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...primaryButtonStyle, textDecoration: 'none' }}
                    onClick={() => {
                      // Sync internship context so essay buttons work on non-Greenhouse ATS pages
                      localStorage.setItem('tandm_active_internship', String(internship.id));
                      window.dispatchEvent(new CustomEvent('tandm:set-internship', { detail: { internship_id: internship.id } }));
                    }}
                  >
                    Open employer portal
                  </a>
                  <button onClick={() => confirmManualCompletion(true)} disabled={loading} style={secondaryButtonStyle}>
                    I submitted it
                  </button>
                  <button onClick={() => setStep('review')} disabled={loading} style={ghostButtonStyle}>
                    Edit application
                  </button>
                </div>
              </div>
            )}

            {step === 'success' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={infoCardStyle}>
                  <p style={sectionTitleStyle}>Application recorded</p>
                  <p style={{ margin: '0 0 8px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
                    {applicationRecordedCopy(application)}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={miniCardStyle}>
                      <span style={{ display: 'block', fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>Status</span>
                      <strong style={{ color: '#0B1220', fontSize: 13 }}>
                        {application?.submission_status === 'submitted_unconfirmed'
                          ? 'Submitted — confirm on employer site'
                          : application?.status || 'submitted'}
                      </strong>
                    </div>
                    <div style={miniCardStyle}>
                      <span style={{ display: 'block', fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>Mode</span>
                      <strong style={{ color: '#0B1220', fontSize: 13 }}>{applyModeLabel}</strong>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={close} style={primaryButtonStyle}>Done</button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const READINESS_TIER_CONFIG = {
  full_automation: { bg: 'var(--tandm-success-bg)', border: 'var(--tandm-success-border)', color: 'var(--tandm-success-fg)', label: 'Full — Tandm can fill this form completely.' },
  assisted_automation: { bg: 'var(--tandm-warning-bg)', border: 'var(--tandm-warning-border)', color: 'var(--tandm-warning-fg)', label: 'Assisted — Tandm fills what it can; review before you submit.' },
  guided_handoff: { bg: 'var(--tandm-info-bg)', border: 'var(--tandm-info-border)', color: 'var(--tandm-info-fg)', label: 'Guided — Tandm prepares your packet and guides the steps.' },
  unsupported: { bg: 'var(--tandm-danger-bg)', border: 'var(--tandm-danger-border)', color: 'var(--tandm-danger-fg)', label: 'Blocked — this portal needs a manual apply for now.' },
  blocked_partial: { bg: 'var(--tandm-danger-bg)', border: 'var(--tandm-danger-border)', color: 'var(--tandm-danger-fg)', label: 'Blocked — partial only; expect manual fields.' },
  full_autofill_ready: { bg: 'var(--tandm-success-bg)', border: 'var(--tandm-success-border)', color: 'var(--tandm-success-fg)', label: 'Full — Tandm can fill this form completely.' },
  ready_with_review: { bg: 'var(--tandm-warning-bg)', border: 'var(--tandm-warning-border)', color: 'var(--tandm-warning-fg)', label: 'Assisted — a few answers need your confirmation.' },
  partial_autofill: { bg: 'var(--tandm-warning-bg)', border: 'var(--tandm-warning-border)', color: 'var(--tandm-warning-fg)', label: 'Assisted — some fields could not be resolved automatically.' },
  blocked: { bg: 'var(--tandm-danger-bg)', border: 'var(--tandm-danger-border)', color: 'var(--tandm-danger-fg)', label: 'Blocked — unsupported fields need manual input.' },
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(11,18,32,0.55)',
  backdropFilter: 'blur(10px)',
  zIndex: 400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const panelStyle = {
  width: 'min(920px, 100%)',
  maxHeight: '90vh',
  overflowY: 'auto',
  background: 'var(--tandm-gradient-modal)',
  borderRadius: 24,
  border: '1px solid rgba(255,255,255,0.95)',
  boxShadow: '0 30px 80px rgba(0,0,0,0.18)',
  padding: 28,
  fontFamily: 'var(--tandm-font-sans)',
};

const eyebrowStyle = {
  margin: '0 0 8px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#4E6C7E',
};

const closeButtonStyle = {
  border: '1px solid #D9E2E6',
  background: '#fff',
  color: '#475569',
  borderRadius: 999,
  padding: '10px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};

const sectionTitleStyle = {
  margin: '0 0 14px',
  fontSize: 16,
  fontWeight: 700,
  color: '#0B1220',
};

const infoCardStyle = {
  background: 'rgba(255,255,255,0.82)',
  border: '1px solid #D9E2E6',
  borderRadius: 18,
  padding: 18,
};

const miniCardStyle = {
  border: '1px solid #EEF2F4',
  borderRadius: 14,
  padding: '12px 13px',
  background: '#FFFFFF',
};

const warningRowStyle = {
  padding: '10px 12px',
  borderRadius: 12,
  background: '#E8F1F3',
  border: '1px solid #D9E2E6',
  color: '#3A5468',
  fontSize: 13,
};

const successPillStyle = {
  padding: '11px 12px',
  borderRadius: 12,
  background: '#ECFDF5',
  border: '1px solid #A7F3D0',
  color: '#065F46',
  fontSize: 13,
  fontWeight: 600,
};

const fieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 14,
};

const labelStyle = {
  display: 'block',
  marginBottom: 7,
  fontSize: 11,
  fontWeight: 700,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inputStyle = {
  width: '100%',
  padding: '11px 13px',
  borderRadius: 12,
  border: '1.5px solid #D9E2E6',
  background: '#fff',
  fontFamily: 'var(--tandm-font-sans)',
  fontSize: 13,
  color: '#0B1220',
  outline: 'none',
};

const checkboxRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  fontSize: 13,
  color: '#1E293B',
  lineHeight: 1.55,
};

const errorBoxStyle = {
  marginBottom: 18,
  padding: '11px 12px',
  borderRadius: 12,
  border: '1px solid #FCA5A5',
  background: '#FEF2F2',
  color: '#991B1B',
  fontSize: 13,
};

const centerStateStyle = {
  minHeight: 220,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  color: '#475569',
  fontSize: 15,
  fontWeight: 500,
};

const primaryButtonStyle = {
  border: 'none',
  background: 'var(--tandm-gradient-cta)',
  color: '#fff',
  borderRadius: 14,
  padding: '13px 18px',
  cursor: 'pointer',
  fontWeight: 700,
  fontFamily: 'var(--tandm-font-sans)',
  fontSize: 14,
};

const secondaryButtonStyle = {
  border: '1px solid #D9E2E6',
  background: '#fff',
  color: '#475569',
  borderRadius: 14,
  padding: '13px 18px',
  cursor: 'pointer',
  fontWeight: 700,
  fontFamily: 'var(--tandm-font-sans)',
  fontSize: 14,
};

const ghostButtonStyle = {
  border: 'none',
  background: 'transparent',
  color: '#64748B',
  borderRadius: 14,
  padding: '13px 8px',
  cursor: 'pointer',
  fontWeight: 700,
  fontFamily: 'var(--tandm-font-sans)',
  fontSize: 14,
};
